import * as XLSX from 'xlsx';

export interface ParsedSlotRow {
  date: string;
  startTime: string;
  endTime: string;
  instructorId: number | null;
  instructorName: string | null;
  topicMasterId: number | null;
  topicName: string | null;
  agenda: string | null;
  batches: string | null; // Comma-separated batch codes/names
  isOnline: boolean; // Default false, true when "true" is entered
}

export interface ParsedPatternRow {
  dayOfWeek: number; // 1-7 (1=Sunday)
  startTime: string;
  endTime: string;
  instructorId: number | null;
  instructorName: string | null;
  topicMasterId: number | null;
  topicName: string | null;
  agenda: string | null;
}

const DAY_NAME_MAP: Record<string, number> = {
  sun: 1, sunday: 1,
  mon: 2, monday: 2,
  tue: 3, tuesday: 3,
  wed: 4, wednesday: 4,
  thu: 5, thursday: 5,
  fri: 6, friday: 6,
  sat: 7, saturday: 7,
};

/**
 * Parse an ArrayBuffer into an XLSX workbook
 */
export function parseExcelFile(buffer: ArrayBuffer): XLSX.WorkBook {
  return XLSX.read(buffer, { type: 'array', cellDates: true });
}

/**
 * Check if a date string YYYY-MM-DD represents a valid calendar date
 */
function isValidDate(dateStr: string): boolean {
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);

  // Check month range
  if (month < 1 || month > 12) return false;

  // Check day range
  if (day < 1 || day > 31) return false;

  // Create date and verify it matches input (catches invalid dates like 2023-02-30)
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year &&
         date.getMonth() === month - 1 &&
         date.getDate() === day;
}

/**
 * Normalize an Excel date value to YYYY-MM-DD string
 */
export function normalizeDate(value: any): string | null {
  if (!value && value !== 0) return null;

  // Already a Date object (xlsx cellDates: true)
  if (value instanceof Date) {
    // Check if it's a valid date
    if (isNaN(value.getTime())) return null;

    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;

    // Validate the constructed date
    return isValidDate(dateStr) ? dateStr : null;
  }

  const str = String(value).trim();
  if (!str) return null;

  // ISO format: YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    return isValidDate(str) ? str : null;
  }

  // DD-MM-YYYY (with hyphens)
  const dmyHyphenMatch = str.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dmyHyphenMatch) {
    const [, day, month, year] = dmyHyphenMatch;
    const dateStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    return isValidDate(dateStr) ? dateStr : null;
  }

  // DD/MM/YYYY (with slashes)
  const dmySlashMatch = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmySlashMatch) {
    const [, day, month, year] = dmySlashMatch;
    const dateStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    return isValidDate(dateStr) ? dateStr : null;
  }

  // Try parsing as a generic date
  const parsed = new Date(str);
  if (!isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    const d = String(parsed.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;
    return isValidDate(dateStr) ? dateStr : null;
  }

  return null;
}

/**
 * Normalize a time value to HH:mm string
 */
export function normalizeTime(value: any): string | null {
  if (!value && value !== 0) return null;

  // Excel fractional day (e.g., 0.375 = 09:00)
  if (typeof value === 'number' && value >= 0 && value < 1) {
    const totalMinutes = Math.round(value * 24 * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  // Date object from Excel (time-only cells come as dates with 1899-12-30 base)
  if (value instanceof Date) {
    const h = String(value.getHours()).padStart(2, '0');
    const m = String(value.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  }

  const str = String(value).trim();
  if (!str) return null;

  // HH:mm:ss → HH:mm
  const hmsMatch = str.match(/^(\d{1,2}):(\d{2}):(\d{2})$/);
  if (hmsMatch) {
    return `${hmsMatch[1].padStart(2, '0')}:${hmsMatch[2]}`;
  }

  // HH:mm
  const hmMatch = str.match(/^(\d{1,2}):(\d{2})$/);
  if (hmMatch) {
    return `${hmMatch[1].padStart(2, '0')}:${hmMatch[2]}`;
  }

  return null;
}

/**
 * Parse a day-of-week value to a number (1=Sunday, 7=Saturday)
 */
export function parseDayOfWeek(value: any): number | null {
  if (!value && value !== 0) return null;

  // Numeric 1-7
  if (typeof value === 'number' && value >= 1 && value <= 7) {
    return value;
  }

  const str = String(value).trim().toLowerCase();
  if (!str) return null;

  // Try numeric string
  const num = parseInt(str, 10);
  if (!isNaN(num) && num >= 1 && num <= 7) return num;

  // Try day name
  return DAY_NAME_MAP[str] || null;
}

/**
 * Get a trimmed string or null from a cell value
 */
function cellString(value: any): string | null {
  if (value === null || value === undefined || value === '') return null;
  return String(value).trim() || null;
}

/**
 * Get a number or null from a cell value
 */
function cellNumber(value: any): number | null {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return isNaN(num) ? null : num;
}

/**
 * Find a header row and map column indices.
 * Returns null if required headers are not found.
 */
function mapHeaders(sheet: XLSX.WorkSheet): { headerRow: number; colMap: Record<string, number> } | null {
  const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');

  for (let r = range.s.r; r <= Math.min(range.s.r + 5, range.e.r); r++) {
    const colMap: Record<string, number> = {};
    for (let c = range.s.c; c <= range.e.c; c++) {
      const cell = sheet[XLSX.utils.encode_cell({ r, c })];
      if (cell && cell.v) {
        const header = String(cell.v).trim().toLowerCase();
        colMap[header] = c;
      }
    }
    // Check if this row looks like a header
    if (colMap['start time'] || colMap['starttime'] || colMap['start']) {
      return { headerRow: r, colMap };
    }
  }
  return null;
}

function getCellValue(sheet: XLSX.WorkSheet, row: number, col: number): any {
  const cell = sheet[XLSX.utils.encode_cell({ r: row, c: col })];
  return cell ? cell.v : null;
}

function findCol(colMap: Record<string, number>, ...names: string[]): number | undefined {
  for (const name of names) {
    if (colMap[name] !== undefined) return colMap[name];
  }
  return undefined;
}

/**
 * Parse the first sheet as date-specific slots
 */
export function parseSlotsSheet(workbook: XLSX.WorkBook): ParsedSlotRow[] {
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) return [];

  const headerInfo = mapHeaders(sheet);
  if (!headerInfo) return [];

  const { headerRow, colMap } = headerInfo;
  const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
  const rows: ParsedSlotRow[] = [];

  const dateCol = findCol(colMap, 'date');
  const startCol = findCol(colMap, 'start time', 'starttime', 'start');
  const endCol = findCol(colMap, 'end time', 'endtime', 'end');
  const instIdCol = findCol(colMap, 'instructor id', 'instructorid');
  const instNameCol = findCol(colMap, 'instructor name', 'instructorname', 'instructor');
  const topicIdCol = findCol(colMap, 'topic id', 'topicid');
  const topicNameCol = findCol(colMap, 'topic name', 'topicname', 'topic');
  const agendaCol = findCol(colMap, 'agenda');
  const batchCodesCol = findCol(colMap, 'batches', 'batch codes', 'batchcodes', 'batch');
  const isOnlineCol = findCol(colMap, 'is online', 'isonline', 'online');

  for (let r = headerRow + 1; r <= range.e.r; r++) {
    const dateVal = dateCol !== undefined ? getCellValue(sheet, r, dateCol) : null;
    const startVal = startCol !== undefined ? getCellValue(sheet, r, startCol) : null;
    const endVal = endCol !== undefined ? getCellValue(sheet, r, endCol) : null;

    // Skip completely empty rows
    if (!dateVal && !startVal && !endVal) continue;

    // Parse isOnline (default false, true only when explicitly "true")
    let isOnline = false;
    if (isOnlineCol !== undefined) {
      const onlineVal = getCellValue(sheet, r, isOnlineCol);
      if (onlineVal) {
        const str = String(onlineVal).trim().toLowerCase();
        isOnline = str === 'true' || str === '1' || str === 'yes';
      }
    }

    rows.push({
      date: normalizeDate(dateVal) || '',
      startTime: normalizeTime(startVal) || '',
      endTime: normalizeTime(endVal) || '',
      instructorId: instIdCol !== undefined ? cellNumber(getCellValue(sheet, r, instIdCol)) : null,
      instructorName: instNameCol !== undefined ? cellString(getCellValue(sheet, r, instNameCol)) : null,
      topicMasterId: topicIdCol !== undefined ? cellNumber(getCellValue(sheet, r, topicIdCol)) : null,
      topicName: topicNameCol !== undefined ? cellString(getCellValue(sheet, r, topicNameCol)) : null,
      agenda: agendaCol !== undefined ? cellString(getCellValue(sheet, r, agendaCol)) : null,
      batches: batchCodesCol !== undefined ? cellString(getCellValue(sheet, r, batchCodesCol)) : null,
      isOnline,
    });
  }

  return rows;
}

/**
 * Parse the first sheet as week pattern entries
 */
export function parsePatternSheet(workbook: XLSX.WorkBook): ParsedPatternRow[] {
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) return [];

  const headerInfo = mapHeaders(sheet);
  if (!headerInfo) return [];

  const { headerRow, colMap } = headerInfo;
  const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
  const rows: ParsedPatternRow[] = [];

  const dayCol = findCol(colMap, 'day', 'day of week', 'dayofweek');
  const startCol = findCol(colMap, 'start time', 'starttime', 'start');
  const endCol = findCol(colMap, 'end time', 'endtime', 'end');
  const instIdCol = findCol(colMap, 'instructor id', 'instructorid');
  const instNameCol = findCol(colMap, 'instructor name', 'instructorname', 'instructor');
  const topicIdCol = findCol(colMap, 'topic id', 'topicid');
  const topicNameCol = findCol(colMap, 'topic name', 'topicname', 'topic');
  const agendaCol = findCol(colMap, 'agenda');

  for (let r = headerRow + 1; r <= range.e.r; r++) {
    const dayVal = dayCol !== undefined ? getCellValue(sheet, r, dayCol) : null;
    const startVal = startCol !== undefined ? getCellValue(sheet, r, startCol) : null;
    const endVal = endCol !== undefined ? getCellValue(sheet, r, endCol) : null;

    // Skip completely empty rows
    if (!dayVal && !startVal && !endVal) continue;

    rows.push({
      dayOfWeek: parseDayOfWeek(dayVal) || 0,
      startTime: normalizeTime(startVal) || '',
      endTime: normalizeTime(endVal) || '',
      instructorId: instIdCol !== undefined ? cellNumber(getCellValue(sheet, r, instIdCol)) : null,
      instructorName: instNameCol !== undefined ? cellString(getCellValue(sheet, r, instNameCol)) : null,
      topicMasterId: topicIdCol !== undefined ? cellNumber(getCellValue(sheet, r, topicIdCol)) : null,
      topicName: topicNameCol !== undefined ? cellString(getCellValue(sheet, r, topicNameCol)) : null,
      agenda: agendaCol !== undefined ? cellString(getCellValue(sheet, r, agendaCol)) : null,
    });
  }

  return rows;
}
